// src/pages/CabinetComposer.jsx
// Cabinet de curiosités, lot 2 : composer un cabinet à partir d'une collection (sans IA).
// Chaque enregistrement crée une nouvelle révision ; on peut revenir à une version précédente.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import {
  CABINET_THEMES, MAX_ITEMS, MAX_CAPTION, themeById,
  isCabinetEnabled, listCabinets, createCabinet, listRevisions, loadRevision, saveRevision,
  cabinetErrorMessage, parseImages, pickImage, mediaRefOf, resolveImageUrl,
  shareRevision, revokeShare, listShares, shareErrorMessage,
} from "../lib/cabinetService";
import { renderCabinetPng } from "../lib/cabinetExport";
import { useCabinetText } from "../lib/cabinetI18n";

const BORDER = "#e5e3df";
const GOLD = "#a9843f";
const sans = "system-ui, sans-serif";
const btn = {
  padding: "8px 14px", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 4,
  fontSize: 13, cursor: "pointer", fontFamily: sans, fontWeight: 500, color: "#1a1a1a",
};
const btnGold = { ...btn, borderColor: GOLD, color: "#fff", background: GOLD, fontWeight: 600 };

function Thumb({ images, mediaRef, alt, style }) {
  const { t } = useCabinetText();
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    resolveImageUrl(pickImage(images, mediaRef)).then((u) => alive && setSrc(u)).catch(() => {});
    return () => { alive = false; };
  }, [images, mediaRef]);
  if (!src) {
    return <div style={{ background: "#d9d5cc", color: "#8a8378", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", ...style }}>{t("noImage")}</div>;
  }
  return <img src={src} alt={alt} loading="lazy" style={{ objectFit: "cover", ...style }} />;
}

export default function CabinetComposer() {
  const { collectionId } = useParams();
  const { user } = useAuth();
  const { t, lang, locale } = useCabinetText();

  const [enabled, setEnabled] = useState(null);
  const [collection, setCollection] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [cabinet, setCabinet] = useState(null);
  const [version, setVersion] = useState(0);
  const [items, setItems] = useState([]); // { artwork_id, media_ref, display:{caption} }
  const [themeId, setThemeId] = useState(CABINET_THEMES[0].id);
  const [revisions, setRevisions] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { kind: 'ok'|'err'|'conflict', text }
  const [loading, setLoading] = useState(true);
  const [shares, setShares] = useState([]);
  const [shareEmail, setShareEmail] = useState("");
  const [shareDays, setShareDays] = useState(90);

  const byId = useMemo(() => new Map(artworks.map((a) => [a.id, a])), [artworks]);
  const theme = themeById(themeId);

  const applyRevision = useCallback((rev) => {
    setVersion(rev?.version || 0);
    setThemeId(rev?.config?.theme || CABINET_THEMES[0].id);
    setItems((rev?.items || []).filter((it) => it.artwork_id).map((it) => ({
      artwork_id: it.artwork_id, media_ref: it.media_ref || null, display: it.display || {},
    })));
    setDirty(false);
  }, []);

  const openCabinet = useCallback(async (cab) => {
    setCabinet(cab);
    const [rev, revs] = await Promise.all([loadRevision(cab.id), listRevisions(cab.id)]);
    applyRevision(rev);
    setRevisions(revs);
    try { setShares(await listShares(cab.id)); } catch { setShares([]); }
  }, [applyRevision]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const ok = await isCabinetEnabled();
      if (!alive) return;
      setEnabled(ok);
      if (!ok) { setLoading(false); return; }
      try {
        const [{ data: col }, { data: arts }, cabs] = await Promise.all([
          supabase.from("collections").select("id,title,client_id").eq("id", collectionId).maybeSingle(),
          supabase.from("artworks_secure").select("id,title,artist,year,images").eq("collection_id", collectionId).order("title"),
          listCabinets(collectionId),
        ]);
        if (!alive) return;
        setCollection(col || null);
        setArtworks(arts || []);
        setNewTitle(t("defaultTitle", col?.title));
        if (cabs[0]) await openCabinet(cabs[0]);
      } catch (e) {
        if (alive) setMsg({ kind: "err", text: cabinetErrorMessage(e, lang) });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  // Rechargement uniquement au changement de collection : un changement de langue ne doit pas rouvrir le cabinet.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId, openCabinet]);

  // Alerte avant de quitter avec des changements non enregistrés.
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const touch = (fn) => { fn(); setDirty(true); setMsg(null); };

  const add = (a) => {
    if (items.length >= MAX_ITEMS || items.some((i) => i.artwork_id === a.id)) return;
    const img = pickImage(a.images);
    touch(() => setItems((l) => [...l, { artwork_id: a.id, media_ref: mediaRefOf(img), display: {} }]));
  };
  const remove = (idx) => touch(() => setItems((l) => l.filter((_, i) => i !== idx)));
  const move = (idx, d) => touch(() => setItems((l) => {
    const j = idx + d;
    if (j < 0 || j >= l.length) return l;
    const c = l.slice();
    [c[idx], c[j]] = [c[j], c[idx]];
    return c;
  }));
  const setCaption = (idx, text) => touch(() => setItems((l) => l.map((it, i) => (i === idx
    ? { ...it, display: { ...it.display, caption: text.slice(0, MAX_CAPTION) } } : it))));
  const cycleImage = (idx) => {
    const it = items[idx];
    const arr = parseImages(byId.get(it.artwork_id)?.images);
    if (arr.length < 2) return;
    const cur = arr.findIndex((x) => mediaRefOf(x) === it.media_ref);
    const next = arr[(cur + 1) % arr.length];
    touch(() => setItems((l) => l.map((x, i) => (i === idx ? { ...x, media_ref: mediaRefOf(next) } : x))));
  };

  const create = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await createCabinet(collectionId, newTitle);
      const cabs = await listCabinets(collectionId);
      const cab = cabs.find((c) => c.id === r.cabinet_id) || cabs[0];
      await openCabinet(cab);
    } catch (e) {
      setMsg({ kind: "err", text: cabinetErrorMessage(e, lang) });
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await saveRevision(cabinet.id, version, { schema_version: 1, theme: themeId }, items);
      setVersion(r.version);
      setDirty(false);
      setRevisions(await listRevisions(cabinet.id));
      setMsg({ kind: "ok", text: t("saved", r.version) });
    } catch (e) {
      const conflict = String(e?.message || "").includes("VERSION_CONFLICT");
      setMsg({ kind: conflict ? "conflict" : "err", text: cabinetErrorMessage(e, lang) });
    } finally { setBusy(false); }
  };

  const restore = async (v) => {
    if (dirty && !window.confirm(t("restoreConfirm"))) return;
    setBusy(true);
    try {
      const rev = await loadRevision(cabinet.id, v);
      const latest = revisions[0]?.version || version;
      applyRevision({ ...rev, version: latest }); // on repart de la dernière version : l'enregistrement crée la suivante
      setDirty(true);
      setMsg({ kind: "ok", text: t("restored", v) });
    } catch (e) {
      setMsg({ kind: "err", text: cabinetErrorMessage(e, lang) });
    } finally { setBusy(false); }
  };

  // Partage : toujours une version ENREGISTRÉE (figée) ; on refuse avec des changements en cours.
  const share = async () => {
    if (dirty) { setMsg({ kind: "err", text: t("saveFirst") }); return; }
    setBusy(true); setMsg(null);
    try {
      const rev = revisions.find((r) => r.version === version);
      const r = await shareRevision(cabinet.id, rev.id, shareEmail, shareDays);
      setShares(await listShares(cabinet.id));
      const link = `${window.location.origin}/cabinets/partages/${r.share_id}`;
      setMsg({ kind: "ok", text: t("inviteDone", shareEmail.trim().toLowerCase(), link) });
      setShareEmail("");
    } catch (e) {
      setMsg({ kind: "err", text: shareErrorMessage(e, lang) });
    } finally { setBusy(false); }
  };
  const revoke = async (id) => {
    if (!window.confirm(t("revokeConfirm"))) return;
    setBusy(true);
    try { await revokeShare(id); setShares(await listShares(cabinet.id)); setMsg({ kind: "ok", text: t("revoked") }); }
    catch (e) { setMsg({ kind: "err", text: shareErrorMessage(e, lang) }); }
    finally { setBusy(false); }
  };

  const exportPng = async () => {
    setBusy(true); setMsg(null);
    try {
      const entries = await Promise.all(items.map(async (it) => {
        const a = byId.get(it.artwork_id);
        return { src: await resolveImageUrl(pickImage(a?.images, it.media_ref)), title: a?.title, artist: a?.artist, caption: it.display?.caption };
      }));
      const { blob, missing } = await renderCabinetPng({ title: cabinet.title, themeId, entries });
      if (!blob) throw new Error("export");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = `${cabinet.title.replace(/[^\w-]+/g, "_")}.png`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setMsg({ kind: missing ? "err" : "ok", text: missing ? t("exportedPartial", missing) : t("exported") });
    } catch {
      setMsg({ kind: "err", text: t("exportFailed") });
    } finally { setBusy(false); }
  };

  const reload = async () => { setBusy(true); try { await openCabinet(cabinet); setMsg(null); } finally { setBusy(false); } };

  if (loading || enabled === null) return <div style={{ padding: 32, fontFamily: sans }}>{t("loading")}</div>;
  if (!enabled) {
    return (
      <div style={{ maxWidth: 560, margin: "48px auto", padding: "0 16px", fontFamily: sans }}>
        <h1 style={{ fontFamily: "Georgia, serif", fontWeight: 400 }}>{t("title")}</h1>
        <p>{t("notOpen")}</p>
        <Link to={`/collections/${collectionId}`}>{t("backToCollection")}</Link>
      </div>
    );
  }
  if (collection && user && collection.client_id !== user.id) {
    return (
      <div style={{ maxWidth: 560, margin: "48px auto", padding: "0 16px", fontFamily: sans }}>
        <p>{t("ownerOnly")}</p>
        <Link to={`/collections/${collectionId}`}>{t("backToCollection")}</Link>
      </div>
    );
  }

  const placed = new Set(items.map((i) => i.artwork_id));

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 16px 64px", fontFamily: sans }}>
      <Link to={`/collections/${collectionId}`} style={{ fontSize: 13, color: "#8a8378" }}>← {collection?.title || t("collectionFallback")}</Link>
      <h1 style={{ fontFamily: "Georgia, serif", fontWeight: 400, margin: "8px 0 4px" }}>
        {cabinet ? cabinet.title : t("title")}
      </h1>

      {msg && (
        <div role="status" style={{
          margin: "12px 0", padding: "10px 14px", borderRadius: 4, fontSize: 13,
          background: msg.kind === "ok" ? "#eef5ea" : "#fbeeea",
          border: `1px solid ${msg.kind === "ok" ? "#bcd3b0" : "#e6b9ac"}`,
        }}>
          {msg.text}{" "}
          {msg.kind === "conflict" && <button type="button" style={{ ...btn, marginLeft: 8 }} onClick={reload}>{t("reload")}</button>}
        </div>
      )}

      {!cabinet ? (
        <div style={{ marginTop: 24, maxWidth: 480 }}>
          <p style={{ fontSize: 14, color: "#555" }}>
            {t("intro")}
          </p>
          <label style={{ fontSize: 12, color: "#8a8378" }}>{t("titleLabel")}</label>
          <input value={newTitle} maxLength={120} onChange={(e) => setNewTitle(e.target.value)}
            style={{ display: "block", width: "100%", padding: 9, margin: "4px 0 12px", border: `1px solid ${BORDER}`, borderRadius: 4, fontSize: 14 }} />
          <button type="button" style={btnGold} disabled={busy || !newTitle.trim()} onClick={create}>{t("create")}</button>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", margin: "12px 0 20px" }}>
            <span style={{ fontSize: 12, color: "#8a8378" }}>{t("background")}</span>
            {CABINET_THEMES.map((th) => (
              <button key={th.id} type="button" aria-pressed={th.id === themeId}
                onClick={() => touch(() => setThemeId(th.id))}
                style={{ ...btn, padding: "5px 10px", borderColor: th.id === themeId ? GOLD : BORDER, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 14, height: 14, borderRadius: 3, background: th.wall, border: `2px solid ${th.frame}` }} />{t(th.key)}
              </button>
            ))}
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: dirty ? "#b4572f" : "#8a8378" }}>
              {dirty ? t("unsaved") : t("version", version)}
            </span>
            <button type="button" style={btn} disabled={busy || items.length === 0} onClick={exportPng}>{t("exportPng")}</button>
            <button type="button" style={btnGold} disabled={busy || !dirty} onClick={save}>{t("save")}</button>
          </div>

          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
            {/* Œuvres de la collection */}
            <section style={{ flex: "1 1 280px", minWidth: 0 }} aria-label={t("collectionWorksAria")}>
              <div style={{ fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8a8378", fontWeight: 600, marginBottom: 8 }}>
                {t("collectionWorks", artworks.length)}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(110px,1fr))", gap: 8 }}>
                {artworks.map((a) => (
                  <button key={a.id} type="button" disabled={placed.has(a.id)} onClick={() => add(a)}
                    title={placed.has(a.id) ? t("alreadyHung") : t("hang")}
                    style={{ ...btn, padding: 4, textAlign: "left", opacity: placed.has(a.id) ? 0.4 : 1 }}>
                    <Thumb images={a.images} alt={a.title || ""} style={{ width: "100%", height: 90, display: "flex" }} />
                    <div style={{ fontSize: 11, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title || t("untitled")}</div>
                  </button>
                ))}
              </div>
            </section>

            {/* Accrochage */}
            <section style={{ flex: "2 1 460px", minWidth: 0 }} aria-label={t("hangingAria")}>
              <div style={{ background: theme.wall, borderRadius: 6, padding: 20, minHeight: 240 }}>
                {items.length === 0 && (
                  <p style={{ color: theme.ink, opacity: 0.7, fontSize: 14, margin: 0 }}>{t("emptyWall")}</p>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))", gap: 18 }}>
                  {items.map((it, idx) => {
                    const a = byId.get(it.artwork_id);
                    return (
                      <figure key={it.artwork_id} style={{ margin: 0, color: theme.ink }}>
                        <div style={{ border: `4px solid ${theme.frame}`, background: "#000", boxShadow: "0 6px 14px rgba(0,0,0,.35)" }}>
                          <Thumb images={a?.images} mediaRef={it.media_ref} alt={a?.title || ""} style={{ width: "100%", height: 170, display: "flex" }} />
                        </div>
                        <figcaption style={{ fontSize: 12, marginTop: 6 }}>
                          <strong style={{ fontFamily: "Georgia, serif", fontWeight: 400 }}>{a?.title || t("removedWork")}</strong>
                          {a?.artist ? <span style={{ opacity: 0.75 }}> — {a.artist}</span> : null}
                          <textarea value={it.display?.caption || ""} maxLength={MAX_CAPTION} rows={2}
                            placeholder={t("captionPlaceholder")} aria-label={t("captionFor", a?.title || t("theWork"))}
                            onChange={(e) => setCaption(idx, e.target.value)}
                            style={{ width: "100%", marginTop: 4, fontSize: 12, padding: 4, borderRadius: 3, border: "none", resize: "vertical", boxSizing: "border-box" }} />
                          <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                            <button type="button" style={{ ...btn, padding: "2px 8px" }} onClick={() => move(idx, -1)} disabled={idx === 0} aria-label={t("back")}>←</button>
                            <button type="button" style={{ ...btn, padding: "2px 8px" }} onClick={() => move(idx, 1)} disabled={idx === items.length - 1} aria-label={t("forward")}>→</button>
                            {parseImages(a?.images).length > 1 && (
                              <button type="button" style={{ ...btn, padding: "2px 8px" }} onClick={() => cycleImage(idx)}>{t("otherView")}</button>
                            )}
                            <button type="button" style={{ ...btn, padding: "2px 8px", marginLeft: "auto" }} onClick={() => remove(idx)} aria-label={t("remove")}>✕</button>
                          </div>
                        </figcaption>
                      </figure>
                    );
                  })}
                </div>
              </div>

              <details style={{ marginTop: 16, fontSize: 13 }} open={shares.some((s) => !s.revoked_at)}>
                <summary style={{ cursor: "pointer" }}>{t("shareSummary", shares.filter((s) => !s.revoked_at).length)}</summary>
                <p style={{ color: "#555", margin: "8px 0" }}>
                  {t("shareHelp", version)}
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input type="email" value={shareEmail} placeholder={t("emailPlaceholder")} aria-label={t("emailAria")}
                    onChange={(e) => setShareEmail(e.target.value)}
                    style={{ flex: "1 1 220px", padding: 8, border: `1px solid ${BORDER}`, borderRadius: 4, fontSize: 13 }} />
                  <select value={shareDays} onChange={(e) => setShareDays(Number(e.target.value))} aria-label={t("durationAria")}
                    style={{ padding: 8, border: `1px solid ${BORDER}`, borderRadius: 4, fontSize: 13 }}>
                    {[7, 30, 90, 365].map((n) => <option key={n} value={n}>{t("days", n)}</option>)}
                  </select>
                  <button type="button" style={btnGold} disabled={busy || !shareEmail.trim() || version < 1} onClick={share}>{t("invite")}</button>
                </div>
                {shares.length > 0 && (
                  <ul style={{ listStyle: "none", padding: 0, margin: "10px 0 0" }}>
                    {shares.map((s) => {
                      const expired = new Date(s.expires_at) <= new Date();
                      const status = s.revoked_at ? t("statusRevoked") : expired ? t("statusExpired") : t("statusUntil", new Date(s.expires_at).toLocaleDateString(locale));
                      return (
                        <li key={s.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "4px 0", opacity: s.revoked_at || expired ? 0.55 : 1 }}>
                          <span>{s.recipient_email} · {status} · {t("views", s.views)}</span>
                          {!s.revoked_at && !expired && <button type="button" style={{ ...btn, padding: "2px 8px" }} disabled={busy} onClick={() => revoke(s.id)}>{t("revoke")}</button>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </details>

              {revisions.length > 1 && (
                <details style={{ marginTop: 16, fontSize: 13 }}>
                  <summary style={{ cursor: "pointer" }}>{t("previousVersions", revisions.length)}</summary>
                  <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0" }}>
                    {revisions.map((r) => (
                      <li key={r.id} style={{ display: "flex", gap: 12, alignItems: "center", padding: "4px 0" }}>
                        <span>{t("versionLine", r.version, new Date(r.created_at).toLocaleString(locale))}</span>
                        {r.version !== version && <button type="button" style={{ ...btn, padding: "2px 8px" }} disabled={busy} onClick={() => restore(r.version)}>{t("restore")}</button>}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
